import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Task, isTaskOverdue, tasksCountQuery, tasksQuery } from "services/tasks";
import {
  Notification,
  isHighSeverityNotification,
  notificationsQuery,
} from "services/notifications";
import bellImg from "images/bell.svg";
import notchImg from "images/notch.svg";
import { useTranslation } from "react-i18next";
import { activateOnKeys } from "utils/a11y";
import NotificationsList from "./components/NotificationsList/NotificationsList";
import "./UserNotifications.css";

const UPDATE_PERIOD = 5 * 60 * 1000; // 5 minutes

const isHighSeverityTaskOrNotification = (
  tasks: Task[],
  notifications: Notification[]
) => {
  return (
    tasks.some(task => isTaskOverdue(task)) ||
    notifications.some(notification => isHighSeverityNotification(notification))
  );
};

export default function UserNotifications() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const tasksResult = useQuery({ ...tasksQuery(), staleTime: UPDATE_PERIOD });
  const tasksCountResult = useQuery({
    ...tasksCountQuery(),
    staleTime: UPDATE_PERIOD,
    refetchInterval: UPDATE_PERIOD,
  });
  const notificationsResult = useQuery({
    ...notificationsQuery(),
    staleTime: UPDATE_PERIOD,
    refetchInterval: UPDATE_PERIOD,
  });
  const [showDetails, setShowDetails] = useState(false);
  const container = useRef<HTMLDivElement>(null);

  const tasks = tasksResult.data?.list ?? [];
  const notifications = notificationsResult.data?.list ?? [];
  // The poll reads only the count, so the badge takes whichever read is newer.
  const numberOfTasks =
    tasksCountResult.dataUpdatedAt > tasksResult.dataUpdatedAt
      ? (tasksCountResult.data ?? 0)
      : tasks.length;
  const numberOfNotifications = notifications.length;
  const isHighSeverity = isHighSeverityTaskOrNotification(tasks, notifications);

  useEffect(() => {
    document.addEventListener("mousedown", handleClick, false);
    return () => {
      document.removeEventListener("mousedown", handleClick, false);
    };
    // Mount-only: the listener lasts as long as the component.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: tasksQuery.baseKey });
    void queryClient.invalidateQueries({ queryKey: notificationsQuery.baseKey });
  };

  const handleClick = (evt: MouseEvent) => {
    if (evt.target instanceof Node && !container.current?.contains(evt.target)) {
      // Click outside, close notifications.
      closeDetails();
    }
  };

  const handleIconClick = () => {
    if (!showDetails) {
      // If we open details
      void notificationsResult.refetch();
      void tasksResult.refetch().then(result => {
        if (result.isSuccess) {
          setShowDetails(true);
        }
      });
    } else {
      setShowDetails(false);
    }
  };

  const closeDetails = () => {
    setShowDetails(false);
  };

  return (
    <div className="user-notifications" ref={container}>
      <div className={"icon-container" + (isHighSeverity ? " has-overdue" : "")}>
        <div
          onClick={handleIconClick}
          role="button"
          tabIndex={0}
          aria-haspopup="true"
          aria-expanded={showDetails}
          aria-label={t("Common.pageHeader.notifications")}
          onKeyDown={activateOnKeys(handleIconClick)}
        >
          <div className={"badge-container"}>
            {showDetails && (
              <NotificationsContainer
                tasks={tasks}
                notifications={notifications}
                closeDetails={closeDetails}
                refresh={refresh}
              />
            )}
            <img src={bellImg} alt="bell" />
            {numberOfTasks + numberOfNotifications > 0 && (
              <span className="badge">{numberOfTasks + numberOfNotifications}</span>
            )}
            {isHighSeverity && <span className="exclaimer">!</span>}
          </div>
        </div>
      </div>
    </div>
  );
}

interface NotificationsContainerProps {
  tasks: Task[];
  notifications: Notification[];
  closeDetails: () => void;
  refresh: () => void;
}

function NotificationsContainer({
  tasks,
  notifications,
  closeDetails,
  refresh,
}: NotificationsContainerProps) {
  const { t } = useTranslation();

  return (
    <div className="notifications-container">
      <img src={notchImg} alt="" className="notch" />
      <div className="notifications-header">
        <img src={bellImg} alt="bell" />
        {t("Common.pageHeader.notificationsTitle")}
      </div>
      <NotificationsList
        tasks={tasks}
        notifications={notifications}
        closeDetails={closeDetails}
        refresh={refresh}
      />
    </div>
  );
}
